import React, {
    useEffect,
    useState,
    useMemo,
    useCallback,
    useRef,
} from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import DateInput from '@mlb/react-patterns/components/DateInput/dateInput';
import {
    ErrorMsg,
    Wrapper as InputWrapper,
} from '@mlb/react-patterns/components/DateInput/dateInput.style';
import COLORS from '@mlb/react-patterns/style/universal.color';

import { DateUtils } from 'bdvp-common/src/utils';
import { useDidUpdate } from 'bdvp-common/src/hooks';

const Wrapper = styled.div`
    ${({ isValidRange }) => !isValidRange && `
        ${InputWrapper} {
            border: 1px solid ${COLORS.inputInvalidOnDark};
        }
    `}
`;

const Field = styled.div`
    margin-bottom: 10px;
`;

const Label = styled.div`
    margin-bottom: 6px;
    color: ${({ theme, disabled }) => disabled ? COLORS.mlbGrey5 : theme.primaryTextColor};
    font-family: ${({ theme }) => theme.fonts.family.primary};
    font-size: 14px;
    font-weight: 600;
`;

const buildInputState = (timestamp) => {

    if(!timestamp) {

        return {
            date: null,
            string: '',
            valid: false,
        };

    }

    const date = DateUtils.parseTimestampToDate(timestamp);

    return {
        date,
        string: timestamp,
        valid: DateUtils.isValid(date),
    };

};

const DateRange = (props) => {

    const {
        fields,
        onChange,
        disabled,
    } = props;
    const {
        selected,
        ...field
    } = fields[0];
    const selectedValue = selected[0]?.value || '';
    const [
        fromValue,
        toValue,
    ] = useMemo(() => selectedValue.replace(/{|}|"| /g, '').split(','), [selectedValue]);
    const [
        range,
        setRange,
    ] = useState({
        from: buildInputState(fromValue),
        to: buildInputState(toValue),
    });
    const [
        isValidRange,
        setIsValidRange,
    ] = useState(true);
    const preventStateChangeOnUpdate = useRef(false);

    useDidUpdate(() => {

        if (preventStateChangeOnUpdate.current) {

            return;

        }

        setRange({
            from: buildInputState(fromValue),
            to: buildInputState(toValue),
        });

    }, [
        fromValue,
        toValue,
        preventStateChangeOnUpdate,
    ]);

    useEffect(() => {

        if (disabled) {

            setRange({
                from: buildInputState(),
                to: buildInputState(),
            });

        }

    }, [disabled]);

    const onChangeHandler = (key, data) => {

        if(disabled) {

            return;

        }

        setIsValidRange(true);

        setRange({
            ...range,
            [key]: data,
        });

    };

    const onBlurHandler = () => {

        // If blurs out after deleting a field, let's clear filter selection
        if (
            selected[0]
            && (
                range.from.string === '//'
                || range.to.string === '//'
            )
        ) {

            preventStateChangeOnUpdate.current = true;

            onChange({
                changes: selected[0],
                event: 'deselect',
                selected: [],
            }, field);

            return;

        }

        preventStateChangeOnUpdate.current = false;

        // Don't do anything if fields are not valid
        if (
            !range.from.valid
            || !range.to.valid
        ) {

            return;

        }

        // If date range is invalid, set flag and bail
        if (
            DateUtils.isBefore(range.to.date, range.from.date)
            || DateUtils.isAfter(range.from.date, range.to.date)
        ) {

            setIsValidRange(false);
            return;

        }

        const changes = {
            label: `${DateUtils.formatDate(range.from.date, 'en', 'short')} - ${DateUtils.formatDate(range.to.date, 'en', 'short')}`,
            value: `{{ "${range.from.string}", "${range.to.string}" }}`,
        };

        // Apply filter
        onChange({
            changes,
            event: 'select',
            selected: [changes],
        }, field);

    };

    const onResetClickHandler = useCallback(() => {

        if (!selected[0]) {

            return;

        }

        preventStateChangeOnUpdate.current = true;

        onChange({
            changes: selected[0],
            selected: [],
            event: 'deselect',
        }, field);

    }, [
        selected[0],
        onChange,
    ]);

    return (
        <Wrapper
            isValidRange={isValidRange}
        >
            <Field>
                <Label disabled={disabled}>From:</Label>
                <DateInput
                    value={range.from.date}
                    handlers={{
                        onBlur: () => onBlurHandler(),
                        onChange: (data) => onChangeHandler('from', data),
                    }}
                    resetBtnHandlers={{
                        onClick: () => onResetClickHandler(),
                    }}
                    disabled={disabled}
                    inputFormat={disabled ? '' : undefined}
                />
            </Field>
            <Field>
                <Label disabled={disabled}>To:</Label>
                <DateInput
                    value={range.to.date}
                    handlers={{
                        onBlur: () => onBlurHandler(),
                        onChange: (data) => onChangeHandler('to', data),
                    }}
                    resetBtnHandlers={{
                        onClick: () => onResetClickHandler(),
                    }}
                    disabled={disabled}
                    inputFormat={disabled ? '' : undefined}
                />
                {!isValidRange && <ErrorMsg>Invalid Date Range</ErrorMsg>}
            </Field>
        </Wrapper>
    );

};

DateRange.propTypes = {
    onChange: PropTypes.func.isRequired,
    fields: PropTypes.arrayOf(
        PropTypes.shape({
            selected: PropTypes.array.isRequired,
        }).isRequired
    ).isRequired,
    disabled: PropTypes.bool,
};

DateRange.defaultProps = {
    disabled: undefined,
};

export default DateRange;
